export const ROUTES = {
  LOGIN: "/login",
  LOGIN_ERROR: (args: { code: string; message: string }) =>
    `/login?error=${args.code}&message=${encodeURIComponent(args.message)}`,
  SIGNUP: "/signup",
  DASHBOARD: "/dashboard",
  ERROR: "/error",
  TEAM_HOME: ({ orgSlug, teamSlug }: { orgSlug: string; teamSlug: string }) =>
    `/org/${orgSlug}/team/${teamSlug}`,
  TEAM_DOCUMENTS: ({
    orgSlug,
    teamSlug,
  }: {
    orgSlug: string;
    teamSlug: string;
  }) => `/org/${orgSlug}/team/${teamSlug}/documents`,
  TEAM_SETTINGS: ({
    orgSlug,
    teamSlug,
  }: {
    orgSlug: string;
    teamSlug: string;
  }) => `/org/${orgSlug}/team/${teamSlug}/settings`,
  AUDIT_CATALOG_LIST: ({
    orgSlug,
    teamSlug,
  }: {
    orgSlug: string;
    teamSlug: string;
  }) => `/org/${orgSlug}/team/${teamSlug}/audit-catalog`,
  AUDIT_CATALOG_UPLOAD: ({
    orgSlug,
    teamSlug,
  }: {
    orgSlug: string;
    teamSlug: string;
  }) => `/org/${orgSlug}/team/${teamSlug}/audit-catalog/upload`,
  AUDIT_CATALOG_ITEM: ({
    orgSlug,
    teamSlug,
    auditCatalogSlug,
  }: {
    orgSlug: string;
    teamSlug: string;
    auditCatalogSlug: string;
  }) => `/org/${orgSlug}/team/${teamSlug}/audit-catalog/${auditCatalogSlug}`,

  ORG_NEW: `/org/new`,
  ORG_HOME: ({ orgSlug }: { orgSlug: string }) => `/org/${orgSlug}`,
  ORG_SETTINGS: ({ orgSlug }: { orgSlug: string }) =>
    `/org/${orgSlug}/settings`,

  ORG_BILLING: ({ orgSlug }: { orgSlug: string }) => `/org/${orgSlug}/billing`,
  ORG_BILLING_CANCEL: ({ orgSlug }: { orgSlug: string }) =>
    `/org/${orgSlug}/billing/cancel`,
  ORG_BILLING_REACTIVATE: ({ orgSlug }: { orgSlug: string }) =>
    `/org/${orgSlug}/billing/reactivate`,
  ORG_BILLING_MANAGE: ({ orgSlug }: { orgSlug: string }) =>
    `/org/${orgSlug}/billing/manage`,
  ORG_BILLING_UPDATE: ({ orgSlug }: { orgSlug: string }) =>
    `/org/${orgSlug}/billing/update`,
  ORG_BILLING_CHECKOUT: ({ orgSlug }: { orgSlug: string }) =>
    `/org/${orgSlug}/billing/checkout`,

  ORG_AUDIT_PROGRESS: ({ orgSlug }: { orgSlug: string }) =>
    `/org/${orgSlug}/audit-progress`,
  LIVE_AUDIT: ({ orgSlug, teamSlug }: { orgSlug: string; teamSlug: string }) =>
    `/org/${orgSlug}/team/${teamSlug}/live-audit`,

  INVITE: ({ token }: { token: string }) => `/invite/${token}`,
  INVITE_RESPONSE: ({ token }: { token: string }) => `/invite/${token}/accept`,
  ORG_FREE_TRIAL: ({ orgSlug }: { orgSlug: string }) =>
    `/org/${orgSlug}/free-trial`,
  SUPER_ADMIN_DASHBOARD: "/super-admin/dashboard",
  ORG_FREE_TRIAL_SUCCESS: ({ orgSlug }: { orgSlug: string }) =>
    `/org/${orgSlug}/free-trial/success`,
  ORG_UPGRADE_SUCCESS: ({ orgSlug }: { orgSlug: string }) =>
    `/org/${orgSlug}/upgrade/success`,
  AUTH_GOOGLE: "/auth/google",
  AUTH_GOOGLE_CALLBACK: "/auth/google/callback",
  AUTH_GOOGLE_ERROR: "/login?error=oauth_failed",

  // Password Reset routes
  FORGOT_PASSWORD: "/forgot-password",
  RESET_PASSWORD: (token: string) => `/reset-password/${token}`,
  RESET_PASSWORD_ERROR: (error?: string) =>
    `/reset-password/error${error ? `?error=${error}` : ""}`,

  STRIPE_WEBHOOK: "/webhooks/stripe",

  // Legal pages
  PRIVACY_POLICY: ({ lang }: { lang?: string }) =>
    `/legal/privacy-policy${lang ? `?lang=${lang}` : ""}`,
  DATA_PROCESSING_AGREEMENT: ({ lang }: { lang?: string }) =>
    `/legal/data-processing-agreement${lang ? `?lang=${lang}` : ""}`,
  TERMS_AND_CONDITIONS: ({ lang }: { lang?: string }) =>
    `/legal/terms-and-conditions${lang ? `?lang=${lang}` : ""}`,
} as const;

export const SIDEBAR_WIDTH = {
  rail: "w-20",
  team: "md:w-60",
  organization: "md:w-80",
} as const;

// External links (legacy - now hosted internally)
export const LEGAL_LINKS = {
  PRIVACY_POLICY: "/legal/privacy-policy",
  DPA: "/legal/data-processing-agreement",
  TERMS: "/legal/terms-and-conditions",
} as const;

export const GOOGLE_OAUTH_ROUTES = {
  AUTH_GOOGLE_TOKEN: "https://oauth2.googleapis.com/token",
  AUTH_GOOGLE_USERINFO: "https://www.googleapis.com/oauth2/v2/userinfo",
} as const;

// AWS LLM Configuration
export const AWS_LLM_CONFIG = {
  maxTokens: 4000,
  temperature: 0.1,
} as const;

export const CLAUDE_LLM_CONFIG = {
  modelName: "claude-sonnet-4-5-20250929",
  maxTokens: 8000,
  temperature: 0.1,
} as const;

export const CLAUDE_SHEET_CLASSIFICATION_MODEL =
  "claude-sonnet-4-5-20250929" as const;

// Rate limiting configuration
export const RATE_LIMITING = {
  ESTIMATED_OUTPUT_TOKENS: 1000, // Conservative estimate
} as const;

export interface AuditAgentConfig {
  modelName: string;
  maxTokens: number;
  temperature: number;
}

export interface RetrievalConfig {
  topK: number;
  similarityThreshold: number;
  locationAware?: boolean;
}

// For audit catalog processing (upgraded to Claude 4.5)
export const AUDIT_AGENT_DEFAULT_CONFIG: AuditAgentConfig = {
  modelName: "claude-sonnet-4-5-20250929",
  maxTokens: 8000,
  temperature: 0.1,
};

// For question answering agent (upgraded to Claude 4.5)
export const AUDIT_QUESTION_AGENT_CONFIG: AuditAgentConfig = {
  modelName: "claude-sonnet-4-5-20250929",
  maxTokens: 8000,
  temperature: 0.1,
};

export interface AuditQuestionConfig {
  maxAnswerLength: number;
  includeSources: boolean;
}

// Match Live Audit retrieval so catalog answers see the same image/text chunks.
export const DEFAULT_RETRIEVAL_CONFIG: RetrievalConfig = {
  topK: 8,
  similarityThreshold: 0.7,
  locationAware: true,
};

/** Max chunks passed to the audit answer LLM after merge + re-rank. */
export const AUDIT_RETRIEVAL_CONTEXT_TOP_K = 12;

export const DEFAULT_AUDIT_QUESTION_CONFIG: AuditQuestionConfig = {
  maxAnswerLength: 300,
  includeSources: true,
};

// Document processing constants
export const DOCUMENT_PROCESSING = {
  MAX_CONTENT_LENGTH: 2000, // Maximum characters to send to AI for analysis
} as const;

// AI processing constants
export const AI_PROCESSING = {
  BULK_GENERATION_DELAY_MS: 1000, // Delay between bulk generation requests
  MAX_PARALLEL_REQUESTS: 3, // Maximum concurrent AI requests
  MAX_ITEMS_PER_REQUEST: 100, // Maximum items to process in one request
  MAX_FIELD_NAME_LENGTH: 50, // Maximum length for generated field names
} as const;

// Batch processing constants
export const BATCH_PROCESSING = {
  DOCX_BATCH_SIZE: 4000, // Characters per DOCX batch
  PDF_BATCH_SIZE: 2000, // Characters per PDF batch
  EXCEL_BATCH_ROWS: 10, // Rows per Excel batch
  DOCX_LLM_CHUNK_SIZE: 3000, // Characters per LLM chunk for splitting
  DOCX_CHUNK_OVERLAP: 500, // Character overlap between chunks
  DOCX_SAMPLE_SIZE: 10000, // Characters to sample for dynamic regex generation
} as const;

// MIME type constants
export const MIME_TYPES = {
  DOCX: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  EXCEL: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  PDF: "application/pdf",
} as const;

// File extension constants (mirror of Prisma enum for client-side use)
export const FILE_EXTENSIONS = {
  PDF: "PDF",
  DOCX: "DOCX",
  XLSX: "XLSX",
  PPTX: "PPTX",
  TXT: "TXT",
  PNG: "PNG",
  JPG: "JPG",
  JPEG: "JPEG",
} as const;

// Audit catalog upload constants
export const AUDIT_CATALOG = {
  MAX_FILE_SIZE: 50 * 1024 * 1024, // 50MB
  ALLOWED_EXTENSIONS: [
    FILE_EXTENSIONS.XLSX,
    FILE_EXTENSIONS.DOCX,
    FILE_EXTENSIONS.PDF,
  ] as const,
  SUPPORTED_MIME_TYPES: [
    MIME_TYPES.EXCEL,
    MIME_TYPES.DOCX,
    MIME_TYPES.PDF,
  ] as const,
} as const;

// Document upload constants
export const DOCUMENT_UPLOAD = {
  MAX_FILE_SIZE: 50 * 1024 * 1024, // 50MB - enforced server-side by the Fastify multipart limit (server/index.ts)
  ALLOWED_EXTENSIONS: [
    FILE_EXTENSIONS.PDF,
    FILE_EXTENSIONS.DOCX,
    FILE_EXTENSIONS.XLSX,
    FILE_EXTENSIONS.PPTX,
    FILE_EXTENSIONS.PNG,
    FILE_EXTENSIONS.JPG,
    FILE_EXTENSIONS.JPEG,
    FILE_EXTENSIONS.TXT,
  ] as const,
} as const;

// Browsers download Office files rather than render them, so "View" on one
// opens the app's own rendering of the document instead of the stored file.
export const OFFICE_EXTENSIONS = [
  FILE_EXTENSIONS.DOCX,
  FILE_EXTENSIONS.XLSX,
  FILE_EXTENSIONS.PPTX,
] as const;

// Document preview constants
export const DOCUMENT_PREVIEW = {
  // A sheet becomes one HTML table; past these many rows or columns the page
  // is too heavy for a tab, and the whole workbook is a download away.
  MAX_SHEET_ROWS: 2000,
  MAX_SHEET_COLUMNS: 100,
  // The whole page, across every sheet, slide and picture: one response held
  // in memory and one document a tab has to lay out.
  MAX_PAGE_CHARS: 12 * 1024 * 1024,
} as const;

// Team management constants
export const TEAM_CONFIG = {
  INVITATION_EXPIRY_DAYS: 7, // Days until team invitation expires
} as const;

// Document category constants
export const VALID_DOCUMENT_CATEGORIES = [
  "nda",
  "policy",
  "diagram",
  "audit",
  "other",
] as const;
export type DocumentCategory = (typeof VALID_DOCUMENT_CATEGORIES)[number];

// Filter out "audit" category for document updates (audit is reserved for audit catalogs)
export const DOCUMENT_UPDATE_CATEGORIES = VALID_DOCUMENT_CATEGORIES.filter(
  category => category !== "audit"
) as [string, ...string[]];

export const isValidDocumentCategory = (
  category: string
): category is DocumentCategory =>
  VALID_DOCUMENT_CATEGORIES.includes(category as DocumentCategory);

export const TERMS_AND_CONDITIONS_LINK = "/legal/terms-and-conditions";

export const HELP_AND_SUPPORT_LINK = "https://www.aiauditbuddy.com/contact-us";

// Email constants
export const EMAIL_URLS = {
  BLOG: "https://www.aiauditbuddy.com/blog",
  LINKEDIN: "https://www.linkedin.com/company/aiauditbuddy",
  DEMO_VIDEO: "https://www.aiauditbuddy.com",
} as const;
